/**
 * L'espace du dernier compte ouvert sur cette machine : l'utilisateur, dans
 * l'organisation où il travaillait (`<userId>:<organizationId>`).
 *
 * Tout ce que l'app garde par utilisateur — onglets du workspace, chats du
 * panneau, fil de la page Agent, pastilles des onglets — est rangé sous une
 * clé qui contient cet espace. Un utilisateur peut appartenir à plusieurs
 * organisations (OXPIUM-623) : les onglets et les conversations de l'une ne
 * doivent pas se rouvrir dans l'autre. Or `useSettings()` va chercher
 * l'utilisateur par requête : sans ce repère, aucun de ces états ne pourrait
 * être relu avant un aller-retour HTTP, c'est-à-dire pas avant d'avoir déjà
 * affiché autre chose. Celui-ci est lisible tout de suite, donc la
 * restauration tient dans le premier rendu.
 *
 * Il est écrit une fois l'utilisateur réellement connu, et effacé à la
 * déconnexion comme au changement d'organisation : un autre compte sur la
 * même machine ne doit pas hériter des conversations du précédent.
 */
const LAST_USER_KEY = "oxpium:last-user";

/** L'espace d'un utilisateur chargé ; son seul id tant qu'il n'a pas d'organisation. */
export function storageScopeOf(
  user: { id: string; organization_id: string | null } | null | undefined,
): string | null {
  if (!user) return null;
  return user.organization_id ? `${user.id}:${user.organization_id}` : user.id;
}

export function readLastScope(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(LAST_USER_KEY);
  } catch {
    return null;
  }
}

export function rememberLastScope(scope: string): void {
  try {
    window.localStorage.setItem(LAST_USER_KEY, scope);
  } catch {
    // localStorage indisponible — on repartira de l'attente réseau.
  }
}

/** À appeler avant `auth.signOut()` et avant de changer d'organisation. */
export function forgetLastScope(): void {
  try {
    window.localStorage.removeItem(LAST_USER_KEY);
  } catch {
    // ignore
  }
}

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const SCOPED = new RegExp(`(${UUID}):${UUID}`, "i");

/**
 * La clé d'avant OXPIUM-623, rangée par utilisateur seulement
 * (`workspace-tabs:<userId>` pour `workspace-tabs:<userId>:<orgId>`). `null`
 * si la clé ne porte pas d'organisation.
 */
function legacyKeyOf(key: string): string | null {
  const legacy = key.replace(SCOPED, "$1");
  return legacy === key ? null : legacy;
}

/**
 * Lit une clé rangée par espace. Avant OXPIUM-623 personne n'avait deux
 * organisations : l'ancienne clé, rangée par utilisateur seulement, revient
 * à la première organisation qui la lit — celle où l'utilisateur travaillait.
 */
export function readScopedItem(key: string): string | null {
  try {
    const value = window.localStorage.getItem(key);
    if (value !== null) return value;
    const legacy = legacyKeyOf(key);
    return legacy ? window.localStorage.getItem(legacy) : null;
  } catch {
    return null;
  }
}

/**
 * Écrit une clé rangée par espace, et efface l'ancienne : une fois adoptée
 * par une organisation, elle ne doit pas se rouvrir dans une autre. Peut
 * lever (quota) comme `setItem`.
 */
export function writeScopedItem(key: string, value: string): void {
  window.localStorage.setItem(key, value);
  const legacy = legacyKeyOf(key);
  if (legacy) window.localStorage.removeItem(legacy);
}
